import React, { memo, useEffect, useMemo, useCallback, useContext } from 'react';
import { FlagRounded as FlagIcon } from '@material-ui/icons';
import moment from 'moment';
import {
  PRESCRIBER, DROPDOWN,
  INTAKE, ADD_PRODUCT_INFO, PRODUCT_INFO,
  NOT_AVAILABLE,
  INFO_CARD_THEME, PATIENT,
  KIT, DOSE_SPOT,
} from '../../constants/patientChart.constant';
import { PatientChartContext } from '../../contexts/patientChart';
import { PatientChartControllerHOC } from '../../controllers/patientChart';
import {
  Flags, NavBar, NavKey, SelectedNavItem, OrderHeading,
  NextOrder, InfoCard, TabDetails, TabsWrapper,
  StartVisit, Actions, Products, ExtendedInfo, enabledNavItems,
  ICD10Codes,
} from '../../components/patientChart';
import { PageHeading, useAvatarTitle } from '../../components/PageHeading';
import useNavItems from '../../hooks/useNavItems';
import useVisits from '../../hooks/useVisits';
import useLoggedInUser from '../../hooks/useLoggedInUser';
import { getAMPM, getCurrentAndTargetDate, getHours, round, getDateTime } from '../../utils/time';
import { generateEmailTemplateList } from '../../utils/emailTemplates';
import { FEATURE } from '../../utils/feature';
import { flattenWithoutComma } from '../../utils/flatten';
import { sortedTextArray } from '../../utils/sortBy';
import { ColumnType, Order, sort } from '../../components//subComponents/table';
import { SAVED_FLAGS } from '../../constants/visits.constant';
import Logo from '../../assets/logo.png';
import './styles.css';

const hasFeature = FEATURE();

const NavItems = enabledNavItems([
  NavKey.home,
  NavKey.sv,
  NavKey.ao,
  NavKey.tasks,
  NavKey.pcm,
  NavKey.so,
]);

const BottomNavItems = enabledNavItems([
  NavKey.v1,
]);

export const PatientChart = memo(({
  match,
  history,
  scheduledVisits,
  asyncOrders,
  orders = {},
  productInfo = PRODUCT_INFO(),
  addProductInfo = ADD_PRODUCT_INFO(),
  selectedPrescriberInfo: prescriber = PRESCRIBER(),
  selectedPatientInfo: patient = PATIENT(),
  patientUploads = {},
  changeProducts = {},
  emailSubjects = {},
  emailReasons = {},
  callSummary = {},
  sendOrderToCSReasons: sendToPSReasons = [],
  callPatient: callPatientInfo = {},
  textPatient: sendTextInfo = {},
  emailPatient: sendEmailInfo = {},
  callPatientDetails: callingInfo = {},
  textmessageTemplateData: messageTemplates = [],
  emailTemplate: emailTemplates = [],
  emailTemplatesV2 = {},
  approveEmailTemplatesV2 = {},
  denyEmailTemplatesV2 = {},
  consultQuestion: intake = INTAKE(),
  productInfoCache = new Map([
    ['', PRODUCT_INFO()],
  ]),
  productCost = {},
  denialReason = [],
  soapNoteInfo: isSoapNoteAdded = false,
  callDetailSent: videoCallDetails = false,
  isVideoFocused = false,
  callStarted: isVideoCallStarted = false,
  savedFlags = SAVED_FLAGS(),
  saveFlags: saveFlagsInfo = {},
  orderAndPrescriberFlagsV2: orderAndPrescriberFlags = {},
  saveExtendedConsult: saveExtendedConsultInfo = {},
  soapNoteTemplates: soapTemplates = {},
  emailMessages = {},
  cmnTemplateFormField = {},
  approvalInfoV2: approvalInfo = {},
  orderNotes = {},
  patientNotes = {},
  medicalOrderNotes = {},
  medicalPatientNotes = {},
  orderNotesAPI = {},
  patientNotesAPI = {},
  medicalOrderNotesAPI = {},
  medicalPatientNotesAPI = {},
  doseSpotData: doseSpot = DOSE_SPOT(),
  historyAPI = {},
  historyData = [],
  quantityRangeForProduct = {},
  changeProductForOrderAPI = {},
  headerInfoAPI = {},
  tasksInfo = {},
  tasksInfoByOrderId = {},
  icd10CodesByTerms = {},
  icd10CodesByOrderId = {},
  icd10CodesByPatientId = {},
  addICD10CodesAPI = {},
  removeICD10CodesAPI = {},

  GetTasks: getTasks = async () => ({}),
  GetTaskByOrderId: getTasksByOId = async () => ({}),
  UpdateTask: updateTask = async () => ({}),
  GetScheduleVisitsLV: getScheduledVisits = async () => {},
  GetPendingFilesLV: getAsyncOrders = async () => {},
  GetOrderNotes: getOrderNotes = async (requestBody = {}) => {},
  GetPatientNotes: getPatientNotes = async (requestBody = {}) => {},
  GetMedicalPatientNotes: getMedicalPatientNotes = async (requestBody = {}) => {},
  GetMedicalOrderNotes: getMedicalOrderNotes = async (requestBody = {}) => {},
  GetNotes: getCriticalFlags = async () => ({}),
  GetPatientInfo: getPatient = async () => {},
  GetConsultQuestions: getConsultQuestions = async () => {},
  GetProductInfoV3: getProductAndCache = async () => {},
  ResetProductInfoV2: resetProductInfo = async () => {},
  GetSoapNoteInfo: getSoapNote = async () => {},
  GetHeaderInfo: getPrescriber = async () => {},
  GetPatientUploads: getUploads = async () => {},
  GetTextPatientTemplates: getMessageTemplates = async () => {},
  GetEmailReasons: getEmailReasons = async () => {},
  GetEmail: getEmails = async (requestBody = {}) => {},
  GetDenialReasons: getDenialReasons = async () => {},
  GetSubjectAndMailForAllTemplates: getSubjects = async () => {},
  GetCallPatientDetails: getCallingInfo = async () => {},
  GetSendOrderToCSReasons: getSendToPSReasons = async () => {},
  OrderCallSummaryReasons: getCallSummaries = async () => {},
  InsertCallSummary: saveCallSummary = async () => {},
  CallToPatientPhys: callPatient = async () => {},
  TextPatient: sendText = async () => {},
  EmailPatientDetails: sendEmail = async () => {},
  GetProductCost: getProductCost = async () => {},
  ResetProductCost: resetProductCost = async () => {},
  AddProduct: addProduct = async () => {},
  SendOrderToCS: sendToPS = async (requestBody = {}) => {},
  AddNote: addNote = async (requestBody = {}) => {},
  VerifyPIN: verifyPin = async (requestBody = {}) => {},
  ApproveAndMailSubmit: approveOrder = async (requestBody = {}, callback = () => {}, routePath = '', called = true) => {},
  DenialAndEmail: denyOrder = async (requestBody = {}) => {},
  GetTokenAndStartVist: getTokenForVideoCall = async (requestBody = { userId: '' }) => {},
  SetStartVisit: setStartVisit = async (requestBody = { userId: '' }) => {},
  CallDetails: startVisit = async (requestBody = { userId: '' }) => {},
  CallEnd: endVisit = async (requestBody = { userId: '' }) => {},
  LogError: logError = async (requestBody = { userId: '' }) => {},
  SaveFlags: saveFlags = async (requestBody = { userId: '' }) => {},
  GetSavedFlags: getSavedFlags = async () => {},
  ResetOrderAndPrescriberFlags: resetFlags = async () => {},
  ResetSavedFlags: resetSavedFlags = async () => {},
  SaveExtendedConsult: saveExtendedConsult = async (requestBody = []) => {},
  AddSoapNote: addSoapNote = async (requestBody = {}) => {},
  SetAddSoapNoteValue: setSoapNoteAdded = async (requestBody = {}) => {},
  GetPatientMedicalNecessityPDFData: getCmnPdfData = () => {},
  SetCmnTemplateFormField: setCmnTemplateFormField = () => {},
  GetApproveAndEmailPrepInfo: getApprovalInfo = async (requestBody = {}) => {},
  ResetApproveAndEmailPrepInfo: resetApprovalInfo = async () => {},
  RemoveOrdersFromLV: removeOrdersFromLV = (requestBody = {}) => {},
  GetDoseSpot: getDoseSpot = async (requestBody = {}) => {},
  DosespotGetPatientPrescriptionsByOrderRequest: modifyDoseSpotPrescription = async (requestBody = {}) => ({}),
  ResetDoseSpot: resetDoseSpot = async () => {},
  ResetHistoryTab: resetHistory = async () => {},
  GetQtyByProduct: getQuantity = async (requestBody = { OrderId: '', ProductId: '' }) => ({}),
  ChangeProductForOrder: changeProduct = async (requestBody = {}) => ({}),
  LogOff: logOff = async () => ({}),
  ValidateToken: validateToken = async () => ({}),
  GetPDFInfo: getPDF = async () => ({}),
  GetICD10CodesByTerms: getICD10CodesByTerms = async () => ({}),
  ClearICD10CodesByTerms: clearICD10CodesByTerms = async () => {},
  GetICD10CodesByOrderId: getICD10CodesByOId = async () => ({}),
  GetICD10CodesByPatientId: getICD10CodesByUId = async () => ({}),
  RemoveICD10CodeFromCollection: removeICD10CodeFromCollection = async () => ({}),
  AddICD10Codes: addICD10Codes = async () => ({}),
  RemoveICD10Codes: removeICD10Codes = async () => ({}),
}) => {
  const {
    OrderDate: orderDate = '',
    api: productInfoAPI = {},
    productSet = new Set(),
  } = productInfo;
  const { orderId: OrderId, patientId: UserId } = match.params;
  const { DoctorId } = useLoggedInUser();
  const {
    tab,
    setTab,
    visitType,
    noOfRequiredFlags,
    noOfAnsweredFlags,
    noOfPendingTasks,
    setFlag,
    setExtendedConsult,
    setStorefrontHistory,
    setKit,
    fetchJumpStartURL,
    handleLoadProductsListing,
    handleLoadHistoryThenProductListing,
  } = useContext(PatientChartContext);

  const { navItems, bottomNavItems } = useNavItems({
    scheduledVisits: scheduledVisits?.visits?.data || [],
    asyncOrders: asyncOrders?.visits?.data || [],
    tasks: tasksInfo?.data || [],
    items: NavItems,
    bottomItems: BottomNavItems,
    history,
  });

  useVisits({
    prescriber,
    scheduledVisits,
    asyncOrders,
    headerInfoAPI,
    tasksInfo,
    getTasks,
    getPrescriber,
    getPendingVisits: getAsyncOrders,
    getScheduledVisits: getScheduledVisits,
  });

  const { avatarTitle } = useAvatarTitle(prescriber);

  const nextOrder = useMemo(() => {
    const nextOrder = {
      orderId: '',
      orderDate: '',
      orderTime: 'No Visits Scheduled',
      productName: NOT_AVAILABLE,
    };

    const { visits: { loading = false, data: visits = [] } } = scheduledVisits;

    const futureVisits = sort(visits.filter((visit) => {
      const startTime = visit.EndDateTime_UTC.toLocaleString();
      const { cD, tD } = getCurrentAndTargetDate(new Date(startTime));
      const isBefore = moment(`${tD.display} ${round(getHours(tD.time.hours))}:${round(tD.time.minutes)} ${getAMPM(tD.time.hours)}`).isBefore(`${cD.display} ${round(getHours(cD.time.hours))}:${round(cD.time.minutes)} ${getAMPM(cD.time.hours)}`);
      if (!isBefore) return visit;
      return undefined;
    }), 'StartDateTime_UTC', Order.asc, ColumnType.string);

    let targetVisit = futureVisits[0];
    if (targetVisit && `${targetVisit.OrderId}` === OrderId) {
      targetVisit = futureVisits[1];
    }

    if (targetVisit) {
      const { formatted: { date, time } } = getDateTime({ date_UTC: targetVisit.StartDateTime_UTC });
      nextOrder.orderId = `${targetVisit.OrderId}`;
      nextOrder.orderDate = date;
      nextOrder.orderTime = time;
      nextOrder.productName = targetVisit.ProductName;
    }

    return {
      loading,
      ...nextOrder,
    };
  }, [scheduledVisits]);

  // eslint-disable-next-line no-unused-vars
  const handleEmailTemplatesResponse = async ({ data }) => {
    const Templates = data.Data;
    getSubjects({ OrderId, Templates });
  };

  const handlePatientResponse = async ({ data }) => {
    const patient = data.Data;
    const {
      ICD10Codes: _icd10Codes = ['NONE'],
      StorefrontMedications: _storefrontHistory = ['NONE'],
      PersonalMedicatoins: _medications = ['NONE'],
      SeriousIllness: _illnesses = ['NONE'],
      Allergies: _allergies = ['NONE'],
    } = patient;

    const storefrontHistoryArray = flattenWithoutComma(_storefrontHistory || ['NONE']);
    const icd10CodesArray = flattenWithoutComma(_icd10Codes || ['NONE']);
    const medicationsArray = flattenWithoutComma(_medications || ['NONE']);
    const illnessesArray = flattenWithoutComma(_illnesses || ['NONE']);
    const allergiesArray = flattenWithoutComma(_allergies || ['NONE']);

    const sortedStorefrontHistoryArray = sortedTextArray(storefrontHistoryArray, Order.asc);

    const storefrontHistory = sortedStorefrontHistoryArray.join(', ');
    const icd10Codes = icd10CodesArray.join(', ');
    const medications = medicationsArray.join(', ');
    const illnesses = illnessesArray.join(', ');
    const allergies = allergiesArray.join(', ');

    const _extendedConsult = { medications, illnesses, allergies };

    setExtendedConsult({ prev: { ..._extendedConsult }, current: { ..._extendedConsult } });
    setStorefrontHistory({ medications: storefrontHistory, icd10Codes });
  };

  useEffect(() => {
    if (!emailReasons.api.called) {
      getEmailReasons();
    }

    if (hasFeature.IS_SALESFORCE_CONFIGURED) {
      getEmails({ OrderId });
    }

    handleLoadHistoryThenProductListing({ OrderId });
    getPatient({ UserId, OrderId }).then(handlePatientResponse);
    getSoapNote({ OrderId });
    getOrderNotes({ OrderId });
    getPatientNotes({ UserId });
    getMedicalOrderNotes({ OrderId });
    getMedicalPatientNotes({ OrderId });
    getCriticalFlags({ OrderId });
    getConsultQuestions({ OrderId });
    getUploads({ UserId });
    getCallingInfo({ OrderId });
    getCallSummaries();
    getMessageTemplates({ OrderId });
    // getEmailTemplates({ OrderId }).then(handleEmailTemplatesResponse);
    getDenialReasons();
    getSendToPSReasons();
    getSavedFlags({ OrderId });
    getPDF({ OrderId });
    getTasksByOId({ OrderId });
    getICD10CodesByUId({ UserId });
    getICD10CodesByOId({ OrderId });

    return () => {
      resetFlags();
      resetSavedFlags();
      setSoapNoteAdded({ name: 'soapNoteInfo', value: false });
      resetApprovalInfo();
      resetProductInfo();
      resetHistory();
      resetDoseSpot();
    };
  }, []);

  useEffect(() => {
    const { orderId } = nextOrder;
    const _collection = new Map(productInfoCache);
    if (!_collection.has(orderId)) {
      _collection.set(orderId, PRODUCT_INFO());
    }

    const _productInfo = _collection.get(orderId);

    if (orderId && !_productInfo.api.called) {
      getProductAndCache({ OrderId: orderId });
    }
  }, [nextOrder, productInfoCache]);

  useEffect(() => {
    const { api: { called = false, loading = false }, flagsCollection = SAVED_FLAGS().flagsCollection } = savedFlags;

    if (called && !loading && flagsCollection.size > 0) {
      setFlag({
        editMode: false,
        prev: { input: new Map(flagsCollection) },
        current: { input: new Map(flagsCollection) },
      });
    }
  }, [savedFlags]);

  useEffect(() => {
    const { api: { called = false, loading = false }, kitsCollection = KIT() } = approvalInfo;

    if (called && !loading && kitsCollection.size > 0) {
      setKit(kitsCollection);
    }
  }, [approvalInfo]);

  const getNotesTabInfo = useCallback(() => {
    getOrderNotes({ OrderId });
    getPatientNotes({ UserId });
    getMedicalOrderNotes({ OrderId });
    getMedicalPatientNotes({ OrderId });
  }, [OrderId, UserId]);

  const _messageTemplates = useMemo(() => {
    const templates = messageTemplates || [];

    return {
      data: templates.map(({ TemplateId: id, TemplateText: display, TemplateTitle: title }) => ({
        title,
        label: title,
        display,
        value: id,
        id
      })),
    };
  }, [messageTemplates]);

  const _emailTemplates = useMemo(() => {
    const templates = emailTemplates || [];

    return {
      data: templates.map(({ EmailMessageId: id, EmailTemplateTitle: title }) => ({
        title,
        label: title,
        value: id,
        id
      })),
      subjects: emailSubjects.api,
    };
  }, [emailTemplates, emailSubjects]);

  const _emailTemplatesV2 = useMemo(() => {
    const { templateCollection, api = {} } = emailTemplatesV2;
    return {
      data: generateEmailTemplateList(emailTemplatesV2, productSet),
      subjects: {
        ...api,
        data: templateCollection,
      },
    };
  }, [emailTemplatesV2, productSet]);

  const _approveEmailTemplatesV2 = useMemo(() => {
    const { templateCollection, api = {} } = approveEmailTemplatesV2;
    return {
      data: generateEmailTemplateList(approveEmailTemplatesV2, productSet),
      subjects: {
        ...api,
        data: templateCollection,
      },
    };
  }, [approveEmailTemplatesV2, productSet]);

  const _denyEmailTemplatesV2 = useMemo(() => {
    const { templateCollection, api = {} } = denyEmailTemplatesV2;
    return {
      data: generateEmailTemplateList(denyEmailTemplatesV2, productSet),
      subjects: {
        ...api,
        data: templateCollection,
      },
    };
  }, [denyEmailTemplatesV2, productSet]);

  const _emailReasons = useMemo(() => {
    return { ...emailReasons.api, valuesCollection: emailReasons.valuesCollection };
  }, [emailReasons]);

  const _denialReasons = useMemo(() => {
    const _denialReasonsCollection = new Map([
      [-1, DROPDOWN()]
    ]);
    let _reasons = [DROPDOWN()];
    _reasons = denialReason.map((reason = { AltItem: '', Description: '' }) => {
      const id = Number(reason.AltItem);
      const _reason = {
        id,
        value: id,
        label: reason.Description,
        description: reason.Description,
      };
      _denialReasonsCollection.set(_reason.id, _reason);
      return _reason;
    });

    return {
      data: _reasons,
      valuesCollection: _denialReasonsCollection,
    };
  }, [denialReason]);

  const _callSummaryReasons = useMemo(() => {
    const reasons = callSummary?.reasons || [];

    return reasons.map(({ AltItem: id, Description: title }) => ({
      title,
      label: title,
      value: id,
      id
    }));
  }, [callSummary]);

  const _sendToPSReasons = useMemo(() => {
    const _sendToPSReasonsCollection = new Map([
      [-1, DROPDOWN()]
    ]);
    let _reasons = [DROPDOWN()];
    _reasons = sendToPSReasons.map((reason = { AltItem: '', Description: '' }, index) => {
      const _reason = {
        id: index,
        value: index,
        label: reason.AltItem,
        description: reason.Description,
      };
      _sendToPSReasonsCollection.set(_reason.id, _reason);
      return _reason;
    });
    return {
      api: {
        data: _reasons,
      },
      valuesCollection: _sendToPSReasonsCollection,
    };
  }, [sendToPSReasons]);

  const _flagInfoCardTheme = useMemo(() => {
    const { api: { called, loading } } = orderAndPrescriberFlags;

    if (!called) {
      return INFO_CARD_THEME.ALERT;
    }

    if (loading) {
      return INFO_CARD_THEME.ALERT;
    }

    if (noOfRequiredFlags === 0) {
      return INFO_CARD_THEME.INFO;
    }

    if (noOfAnsweredFlags !== noOfRequiredFlags) {
      return INFO_CARD_THEME.ALERT;
    }

    return INFO_CARD_THEME.INFO;
  }, [noOfRequiredFlags, noOfAnsweredFlags, orderAndPrescriberFlags]);

  const { assignedDoctorId, isAssignedDoctorViewing } = useMemo(() => {
    const { api: { data: { DoctorId: assignedDoctorId = 0 } } } = orderAndPrescriberFlags;

    let isAssignedDoctorViewing = false;
    try {
      const doctorId = Number(DoctorId);
      if (doctorId) {
        isAssignedDoctorViewing = assignedDoctorId === doctorId;
      }
    } catch (e) {
      // error handling scenarios
    }

    return {
      assignedDoctorId,
      isAssignedDoctorViewing,
    };
  }, [DoctorId, orderAndPrescriberFlags]);

  const _history = useMemo(() => ({
    ...historyAPI,
    data: historyData || [],
  }), [historyAPI, historyData]);

  const handleTabClick = useCallback((tab) => {
    setTab(tab);
  }, []);

  const handleRoute = useCallback((url = '') => {
    history.push(url);
  }, [history]);

  const PageHeadingComponent = new Map([
    [true, (
      <PageHeading
        logo={Logo}
        message={prescriber.Message}
        avatarTitle={avatarTitle}
        hasActions
        logOff={logOff}
        validateToken={validateToken}
        handler={{
          route: handleRoute,
        }}
      />
    )],
    [false, <></>]
  ]);
  const NavBarComponent = new Map([
    [true, <NavBar items={navItems} bottomItems={bottomNavItems} selectedItem={SelectedNavItem} />],
    [false, <></>]
  ]);
  const OrderHeadingComponent = new Map([
    [true, <OrderHeading orderId={OrderId} orderDate={orderDate} visitType={visitType} scheduledVisit={patient.StartDateTime_UTC} loading={productInfoAPI.loading || patient.api.loading} />],
    [false, <></>]
  ]);
  const ActionsComponent = new Map([
    [true, (
      <Actions
        orderId={Number(OrderId)}
        patientId={Number(UserId)}
        doctorId={Number(DoctorId)}
        assignedDoctorId={assignedDoctorId}
        isAssignedDoctorViewing={isAssignedDoctorViewing}
        patient={patient}
        history={_history}
        product={productInfo}
        productCost={productCost}
        addProductInfo={addProductInfo}
        changeProducts={changeProducts}
        quantityRangeForProduct={quantityRangeForProduct}
        changeProductForOrderAPI={changeProductForOrderAPI}
        intake={intake}
        prescriber={prescriber}
        approvalInfo={approvalInfo}
        medicalOrderNotes={medicalOrderNotes}
        cmnTemplateFormField={cmnTemplateFormField}
        isSoapNoteAdded={isSoapNoteAdded}
        sendToPSReasons={_sendToPSReasons}
        emailTemplatesV2={_emailTemplatesV2}
        approveEmailTemplatesV2={_approveEmailTemplatesV2}
        denyEmailTemplatesV2={_denyEmailTemplatesV2}
        emailReasons={_denialReasons}
        soapTemplates={soapTemplates}
        savedFlags={savedFlags}
        flags={orderAndPrescriberFlags}
        doseSpot={doseSpot}
        verifyPin={verifyPin}
        approveOrder={approveOrder}
        denyOrder={denyOrder}
        getNotesTabInfo={getNotesTabInfo}
        addNote={addNote}
        addSoapNote={addSoapNote}
        getSoapNote={getSoapNote}
        setSoapNoteAdded={setSoapNoteAdded}
        sendToPS={sendToPS}
        orders={orders}
        getScheduledVisits={getScheduledVisits}
        getAsyncOrders={getAsyncOrders}
        getCmnPdfData={getCmnPdfData}
        getApprovalInfo={getApprovalInfo}
        setCmnTemplateFormField={setCmnTemplateFormField}
        removeOrdersFromLV={removeOrdersFromLV}
        getDoseSpot={getDoseSpot}
        resetDoseSpot={resetDoseSpot}
        getProductCost={getProductCost}
        resetProductCost={resetProductCost}
        addProduct={addProduct}
        getQuantity={getQuantity}
        changeProduct={changeProduct}
        getEmails={getEmails}
        modifyDoseSpotPrescription={modifyDoseSpotPrescription}
        handler={{
          route: handleRoute,
          loadProductsListing: handleLoadProductsListing,
        }}
      />
  )],
    [false, <></>]
  ]);
  const NextOrderComponent = new Map([
    [true, <NextOrder productInfoCache={productInfoCache} {...nextOrder} />],
    [false, <></>]
  ]);
  const ProductsComponent = new Map([
    [true, (
      <Products
        data={{
          orderId: Number(OrderId),
          productCost,
          productInfo,
          addProductInfo,
          changeProducts,
          isAssignedDoctorViewing,
          orders,
          hasActions: true,
        }}
        action={{
          getProductCost,
          resetProductCost,
          addProduct,
          getDoseSpot: fetchJumpStartURL,
        }}
      />
    )],
    [false, <></>]
  ]);
  const StartButtonComponent = new Map([
    [true, (
      <StartVisit
        orderId={OrderId}
        patientId={Number(UserId)}
        doctorId={Number(DoctorId)}
        getTokenForVideoCall={getTokenForVideoCall}
        getNotesTabInfo={getNotesTabInfo}
        startVisit={startVisit}
        endVisit={endVisit}
        setStartVisit={setStartVisit}
        logError={logError}
        prescriber={prescriber}
        patient={patient}
        videoCallDetails={videoCallDetails}
        isVideoFocused={isVideoFocused}
        isVideoCallStarted={isVideoCallStarted}
      />
    )],
    [false, <></>]
  ]);
  const TabWrapperComponent = new Map([
    [true, (
      <TabsWrapper
        tab={tab}
        noOfPendingTasks={noOfPendingTasks}
        onClick={handleTabClick}
      />
    )],
    [false, <></>]
  ]);
  const TabDetailsComponent = new Map([
    [true, (
      <TabDetails
        tab={tab}
        data={{
          orderId: OrderId,
          patientId: UserId,
          doctorId: DoctorId,
          intake,
          patientUploads,
          emailMessages,
          notes: {
            order: orderNotes,
            patient: patientNotes,
            medical: {
              order: medicalOrderNotes,
              patient: medicalPatientNotes,
            },
          },
          notesAPI: {
            order: orderNotesAPI,
            patient: patientNotesAPI,
            medical: {
              order: medicalOrderNotesAPI,
              patient: medicalPatientNotesAPI,
            },
          },
          notesToDisplay: [
            { key: 'mo', },
            { key: 'mp', },
            { key: 'o', },
            { key: 'p', },
          ],
          history: _history,
          tasksInfo: tasksInfoByOrderId,
        }}
        action={{
          getTasks,
          getTasksByOId,
          updateTask,
          getNotesTabInfo,
        }}
      />
    )],
    [false, <></>]
  ]);
  const FlagsComponent = new Map([
    [true, (
      <InfoCard
        heading={(
          <div className='flex align-items-center'>
            <FlagIcon fontSize='small' className='color-white m-r-1' />
            <div className='flex'>Flags</div>
          </div>
        )}
        content={
          <Flags
            orderId={Number(OrderId)}
            patientId={Number(UserId)}
            doctorId={Number(DoctorId)}
            product={productInfo}
            savedFlags={savedFlags}
            flags={orderAndPrescriberFlags}
            saveFlags={saveFlags}
            getSavedFlags={getSavedFlags}
            saveFlagsInfo={saveFlagsInfo}
            orders={orders}
            asyncOrders={asyncOrders}
            scheduledVisits={scheduledVisits}
            getNotesTabInfo={getNotesTabInfo}
          />
        }
        theme={_flagInfoCardTheme}
        bottomGutter
      />
    )],
    [false, <></>]
  ]);
  const ICD10CodesComponent = new Map([
    [true, (
      <InfoCard
        heading="Order ICD Codes"
        content={
          <ICD10Codes
            data={{
              orderId: Number(OrderId),
              patientId: Number(UserId),
              product: productInfo,
              icd10CodesByTerms,
              icd10CodesByOrderId,
              icd10CodesByPatientId,
              addICD10CodesAPI,
              removeICD10CodesAPI,
            }}
            action={{
              getICD10CodesByTerms,
              clearICD10CodesByTerms,
              removeICD10CodeFromCollection,
              addICD10Codes,
              removeICD10Codes,
              getICD10CodesByOId,
              getICD10CodesByUId,
              getNotesTabInfo,
            }}
          />
        }
      />
    )],
    [false, <></>],
  ]);
  const ExtendedInfoComponent = new Map([
    [true, (
      <ExtendedInfo
        orderId={Number(OrderId)}
        patientId={Number(UserId)}
        doctorId={Number(DoctorId)}
        patient={patient}
        productInfo={productInfo}
        callingInfo={callingInfo}
        callSummaryReasons={_callSummaryReasons}
        messageTemplates={_messageTemplates}
        emailTemplates={_emailTemplates}
        emailTemplatesV2={_emailTemplatesV2}
        emailReasons={_emailReasons}
        callPatientInfo={callPatientInfo}
        sendTextInfo={sendTextInfo}
        sendEmailInfo={sendEmailInfo}
        icd10Codes={icd10CodesByPatientId}
        orders={orders}
        intake={intake}
        saveExtendedConsultInfo={saveExtendedConsultInfo}
        saveCallSummary={saveCallSummary}
        callPatient={callPatient}
        getNotesTabInfo={getNotesTabInfo}
        sendText={sendText}
        sendEmail={sendEmail}
        getEmails={getEmails}
        saveExtendedConsult={saveExtendedConsult}
      />
    )],
    [false, <></>],
  ]);

  return (
    <div className='patient-chart-container flex flex-column flex-fill'>
      {PageHeadingComponent.get(hasFeature.PAGE_HEADING)}

      <div className='patient-chart-content flex gap-24'>
        {NavBarComponent.get(hasFeature.NAV_BAR)}

        <div className='patient-chart-info-cards flex flex-column gap-12 flex-fill'>
          <div className='flex gap-24 patient-chart-info-section-a'>
            <div className='flex flex-column gap-24 flex-fill'>
              {OrderHeadingComponent.get(hasFeature.ORDER_HEADING)}
              {ActionsComponent.get(hasFeature.ACTIONS)}
            </div>

            {NextOrderComponent.get(hasFeature.NEXT_ORDER)}
          </div>

          <div className='flex gap-12 patient-chart-info-section-b'>
            {ExtendedInfoComponent.get(true)}

            <div className='flex flex-column gap-24 flex-fill'>
              <div className='flex flex-column patient-chart-info-section-b-products-wrapper'>
                <div className='patient-chart-info-section-b-products'>
                  {ProductsComponent.get(hasFeature.PRODUCTS)}
                </div>
              </div>

              <div className='flex gap-24'>
                <div className='flex flex-column gap-12 flex-fill'>
                  {TabWrapperComponent.get(hasFeature.TABS)}
                  {TabDetailsComponent.get(hasFeature.TABS)}
                </div>

                <div className='content-box width-420 min-width-420 patient-chart-info-section-b-tab-details-column-b'>
                  {StartButtonComponent.get(hasFeature.START_BUTTON)}

                  <div className='content-box width-420 min-width-420 scroll-y-auto patient-chart-info-section-b-flags-and-icd-10-codes'>
                    {FlagsComponent.get(hasFeature.FLAGS)}
                    {ICD10CodesComponent.get(hasFeature.ICD10CODES)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex'></div>
      </div>
    </div>
  );
});

export default PatientChartControllerHOC(PatientChart);
